import { PlusIcon } from 'lucide-react';
import React, { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import {
  ProofreadingRule,
  ProofreadingRuleType,
} from '../../../services/types/proofreading-types';
import { ProofreadingEmptyState } from '../common/ProofreadingEmptyState';
import { ProofreadingSkeleton } from '../common/ProofreadingSkeleton';
import { useProofreadingRules } from '../hooks/useProofreadingRules';
import { RuleCard } from './RuleCard';

interface RuleListProps {
  firmId: string;
  onSelectRule?: (ruleId: string) => void;
  onEditRule?: (rule: ProofreadingRule) => void;
  onDeleteRule?: (ruleId: string) => void;
  onCreateRuleClick?: () => void;
}

export const RuleList: React.FC<RuleListProps> = ({
  firmId,
  onSelectRule,
  onEditRule,
  onDeleteRule,
  onCreateRuleClick,
}) => {
  const [activeTab, setActiveTab] = useState<string>('firm');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProofreadingRule | null>(
    null,
  );

  const {
    rules,
    globalRules,
    firmRules,
    isLoading,
    error,
    updateRule,
    deleteRule,
  } = useProofreadingRules(firmId);

  const handleDeleteClick = (rule: ProofreadingRule) => {
    setRuleToDelete(rule);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (ruleToDelete) {
      try {
        await deleteRule(ruleToDelete.id);
        setDeleteConfirmOpen(false);
        setRuleToDelete(null);

        if (onDeleteRule) {
          onDeleteRule(ruleToDelete.id);
        }
      } catch (e) {
        console.error('Failed to delete rule:', e);
      }
    }
  };

  const handleToggleEnabled = async (
    rule: ProofreadingRule,
    enabled: boolean,
  ) => {
    try {
      await updateRule(rule.id, { enabled });
    } catch (e) {
      console.error('Failed to update rule:', e);
    }
  };

  if (isLoading) {
    return <ProofreadingSkeleton type="rule-list" />;
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load proofreading rules. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  const allRulesByType = Object.values(ProofreadingRuleType).reduce(
    (acc, type) => {
      // Sort rules so firm rules appear before global rules
      const sortedRules = rules
        .filter((rule) => rule.rule_type === type)
        .sort((a, b) => {
          // Sort by is_global (firm rules first)
          if (a.is_global !== b.is_global) {
            return a.is_global ? 1 : -1;
          }
          // Then by title alphabetically
          return a.title.localeCompare(b.title);
        });

      acc[type] = sortedRules;
      return acc;
    },
    {} as Record<ProofreadingRuleType, ProofreadingRule[]>,
  );

  const hasRules = rules.length > 0;
  const hasFirmRules = firmRules.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Proofreading Rules</h2>
        <Button onClick={onCreateRuleClick} size="sm" className="gap-1">
          <PlusIcon className="h-4 w-4" />
          Create Rule
        </Button>
      </div>

      {!hasRules ? (
        <ProofreadingEmptyState type="rules" onAction={onCreateRuleClick} />
      ) : (
        <Tabs
          defaultValue="firm"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="w-full mb-4">
            <TabsTrigger value="firm" className="flex-1">
              Firm Rules
            </TabsTrigger>
            <TabsTrigger value="global" className="flex-1">
              Global Rules
            </TabsTrigger>
          </TabsList>

          <TabsContent value="firm" className="mt-0">
            {!hasFirmRules ? (
              <ProofreadingEmptyState
                type="rules"
                onAction={onCreateRuleClick}
              />
            ) : (
              Object.entries(allRulesByType).map(([type, typeRules]) => {
                const firmTypeRules = typeRules.filter(
                  (rule) => !rule.is_global,
                );
                return (
                  firmTypeRules.length > 0 && (
                    <div key={type} className="mb-6">
                      <h3 className="text-lg font-medium mb-3 capitalize">
                        {type.replace('_', ' ')}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {firmTypeRules.map((rule) => (
                          <RuleCard
                            key={rule.id}
                            rule={rule}
                            onEdit={
                              onEditRule ? () => onEditRule(rule) : undefined
                            }
                            onDelete={() => handleDeleteClick(rule)}
                            onToggleEnabled={(enabled) =>
                              handleToggleEnabled(rule, enabled)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )
                );
              })
            )}
          </TabsContent>

          <TabsContent value="global" className="mt-0">
            {globalRules.length === 0 ? (
              <div className="p-4 border border-gray-200 rounded bg-gray-50 text-center text-gray-500">
                No global rules available.
              </div>
            ) : (
              Object.entries(allRulesByType).map(([type, typeRules]) => {
                const globalTypeRules = typeRules.filter(
                  (rule) => rule.is_global,
                );
                return (
                  globalTypeRules.length > 0 && (
                    <div key={type} className="mb-6">
                      <h3 className="text-lg font-medium mb-3 capitalize">
                        {type.replace('_', ' ')}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {globalTypeRules.map((rule) => (
                          <RuleCard key={rule.id} rule={rule} />
                        ))}
                      </div>
                    </div>
                  )
                );
              })
            )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the rule &quot;
              {ruleToDelete?.title}&quot;? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
